import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';

export const useTaskOperations = (onSuccess) => {
  const { t } = useTranslation(['tasks', 'common']);
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [loading, setLoading] = useState({});
  const [error, setError] = useState(null);

  const setActionLoading = (action, isLoading) => {
    setLoading(prev => ({ ...prev, [action]: isLoading }));
  };

  const invalidateQueries = () => {
    // Invalidate all task-related queries to refetch data
    queryClient.invalidateQueries(['assignedByMeTasks']);
    queryClient.invalidateQueries(['assignedToMeTasks']);
    queryClient.invalidateQueries(['task-assignments']);
    queryClient.invalidateQueries(['documents']);
    queryClient.invalidateQueries(['document-activities']);
  };

  const handleError = (error, action) => {
    console.error(`❌ Error ${action}:`, error);
    const message = error.response?.data?.message || t(`tasks:error_${action}`, `Failed to ${action}`);
    console.log(`🚨 Showing error toast for ${action}:`, message);
    setError(message);
    toast.error(message);
  };


  const markReceived = async (task) => {
    setActionLoading('receive', true);
    setError(null);
    
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/${task.id}/receive`,
        { 
          status: 'IN_PROGRESS',
          metadata: {
            task_id: task.id,
            document_id: task.document?.id,
            user_id: user.id,
            user_name: user.full_name || user.username,
            action: 'DOCUMENT_RECEIVED',
            timestamp: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${user.token}` }}
      );

      // Notification is handled by backend
      toast.success(t('tasks:document_received_success'));
      invalidateQueries();
      onSuccess?.(task, 'received');
    } catch (error) {
      handleError(error, 'receive');
    } finally {
      setActionLoading('receive', false);
    }
  };

  const rejectReceipt = async (task, reason) => {
    setActionLoading('rejectReceipt', true);
    setError(null);
    
    try {
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/${task.id}/reject-receipt`,
        { 
          status: 'REJECTED_RECEIPT', 
          reason,
          metadata: {
            task_id: task.id,
            document_id: task.document?.id,
            user_id: user.id,
            user_name: user.full_name || user.username,
            action: 'RECEIPT_REJECTED',
            reason,
            previous_status: task.status,
            timestamp: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${user.token}` }}
      );

      // Notification is handled by backend
      toast.success(t('tasks:receipt_rejected_success'));
      invalidateQueries();
      onSuccess?.(task, 'rejected');
    } catch (error) {
      handleError(error, 'rejectReceipt');
    } finally {
      setActionLoading('rejectReceipt', false);
    }
  };

  const completeTask = async (task, notes) => {
    setActionLoading('complete', true);
    setError(null);
    
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/${task.id}/complete`,
        { notes: notes || '' },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      // Notification is handled by backend
      toast.success(t('tasks:task_completed_success'));
      invalidateQueries();
      onSuccess?.(task, 'completed');
    } catch (error) {
      handleError(error, 'complete');
    } finally {
      setActionLoading('complete', false);
    }
  };

  const rejectTask = async (task, reason) => {
    setActionLoading('reject', true);
    setError(null);
    
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/${task.id}/reject`,
        { 
          status: 'REJECTED', 
          reason,
          metadata: {
            task_id: task.id,
            document_id: task.document?.id,
            user_id: user.id,
            user_name: user.full_name || user.username,
            action: 'TASK_REJECTED',
            reason,
            previous_status: task.status,
            timestamp: new Date().toISOString()
          }
        },
        { headers: { Authorization: `Bearer ${user.token}` }}
      );

      // Notification is handled by backend
      toast.success(t('tasks:task_rejected_success'));
      invalidateQueries();
      onSuccess?.(task, 'rejected');
    } catch (error) {
      handleError(error, 'reject');
    } finally {
      setActionLoading('reject', false);
    }
  };

  const forwardTask = async (task, forwardData) => {
    setActionLoading('forward', true);
    setError(null);
    
    try {
      const payload = {
        recipient: forwardData.recipient,
        reason: forwardData.reason
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/task-assignments/${task.id}/forward`,
        payload,
        { headers: { Authorization: `Bearer ${user.token}` }}
      );

      // Notification is handled by backend
      toast.success(t('tasks:task_forwarded_success'));
      invalidateQueries();
      onSuccess?.(task, 'forwarded');
    } catch (error) {
      handleError(error, 'forward');
    } finally {
      setActionLoading('forward', false);
    }
  };

  return {
    markReceived,
    rejectReceipt,
    completeTask,
    rejectTask,
    forwardTask,
    loading,
    error,
    clearError: () => setError(null)
  };
}; 